import { type FC, memo, type ReactNode, type RefObject, useState } from 'react';

import { pick } from 'lodash-es';
import { type Optional } from 'utility-types';

import { useTheme } from '@mui/material/styles';

import { omit } from '@craft/lib/primitives';

import type * as types from './types';

import { type QuantitativeSize } from '../../types/sizes';
import { Image, type ImageComponent, type ImageProps } from '../images/Image';

import { AvatarWrapper, type AvatarWrapperProps } from './AvatarWrapper';
import * as constants from './constants';
import * as util from './util';

interface InheritedImageProps extends Optional<
  Omit<
    ImageProps,
    | 'aspectRatio'
    | 'blurDataURL'
    | 'children'
    | 'color'
    | 'component'
    | 'maxHeight'
    | 'maxWidth'
    | 'minHeight'
    | 'minWidth'
    | 'size'
    | 'style'
  >,
  'src'
> {
  readonly resolutionSize?: QuantitativeSize<'px'>;
}

export interface ImageAvatarProps
  extends InheritedImageProps, Pick<AvatarWrapperProps, 'sx' | keyof types.AvatarStyleProps> {
  /**
   * A base64 data URL used as the blur placeholder while the image at `src` loads.
   *
   * @see https://nextjs.org/docs/pages/api-reference/components/image#placeholder
   */
  readonly blurDataUrl?: null | string;
  readonly children?: ReactNode;
  readonly component: ImageComponent;
  readonly ref?: RefObject<HTMLDivElement>;
}

type NoImageProps = { [key in keyof InheritedImageProps]?: never };

export interface StructuralAvatarProps
  extends Pick<AvatarWrapperProps, 'sx' | keyof types.AvatarStyleProps>, NoImageProps {
  readonly blurDataUrl?: never;
  readonly children: ReactNode;
  readonly component?: never;
  readonly ref?: RefObject<HTMLDivElement>;
}

export type AvatarProps = ImageAvatarProps | StructuralAvatarProps;

const AvatarWrapperPassThroughProps = [
  'borderColor',
  'color',
  'fontSize',
  'size',
  'sx',
  'ref',
] as const;

const ImageNoPassThroughProps = [
  ...AvatarWrapperPassThroughProps,
  'resolutionSize',
  'backgroundColor',
] as const;

export const Avatar: FC<AvatarProps> = memo(props => {
  const size = props.size ?? constants.DefaultAvatarStyleProps.size;

  const [hasError, setHasError] = useState(false);
  const theme = useTheme();

  if (props.src !== undefined && !hasError) {
    return (
      <AvatarWrapper {...pick(props, ...AvatarWrapperPassThroughProps)} size={size}>
        <Image
          aspectRatio={util.getAvatarAspectRatio(size, props.resolutionSize)}
          {...omit(props, ...ImageNoPassThroughProps, 'blurDataUrl', 'children')}
          {...(props.blurDataUrl ? { blurDataURL: props.blurDataUrl } : {})}
          maxHeight='100%'
          maxWidth='100%'
          onError={e => {
            setHasError(true);
            props.onError?.(e);
          }}
          size={{ height: '100%' }}
          src={props.src}
          style={{
            backgroundColor:
              typeof props.backgroundColor === 'function'
                ? props.backgroundColor(theme)
                : (props.backgroundColor ?? undefined),
            borderRadius: '100%',
            boxSizing: 'border-box',
          }}
        />
      </AvatarWrapper>
    );
  }
  return (
    <AvatarWrapper
      {...pick(props, ...AvatarWrapperPassThroughProps)}
      backgroundColor={props.backgroundColor}
      size={size}
    >
      {props.children}
    </AvatarWrapper>
  );
});
